import styled, { css, keyframes } from 'styled-components'

import { hexToHSL, hslToHex, lightenHSL } from '../../../utils/functions'
import theme from '../../../utils/theme'

export const CornerHexagonBg = ({ primaryColor = '', secondaryColor = '', reversed = false, useWindow = false }) => {
  const primary = primaryColor || theme.colors.teal
  const secondary = secondaryColor || theme.colors.blue
  return (
    <StyledContainer useWindow={useWindow} reversed={reversed}>
      <StyledHexagons x={1} className="first" reversed={reversed} color={primary} />
      <StyledHexagons x={2} className="second" reversed={reversed} color={secondary} />
      {!reversed && <StyledOverlay primaryColor={secondary} secondaryColor={primary} />}
    </StyledContainer>
  )
}

const Hexagons = ({ x, className, reversed, color }) => {
  const hsl = hexToHSL(color)
  return (
    <div x={x} className={className} reversed={reversed}>
      <Hex x={1} className="first" reversed={reversed} color={lightenHSL(hsl, 15)} />
      <Hex x={2} className="second" reversed={reversed} color={lightenHSL(hsl, 5)} />
    </div>
  )
}

const Hex = ({ x, className, reversed, color }) => {
  const hslParts = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)
  const h = parseInt(hslParts[1])
  const s = parseInt(hslParts[2])
  const l = parseInt(hslParts[3])
  const hex = hslToHex(h, s, l)
  return (
    <StyledHex x={x} className={className} reversed={reversed}>
      {/** biome-ignore lint/a11y/noSvgWithoutTitle: TODO */}
      <svg viewBox="0 0 867 812" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          opacity={reversed ? '1' : '0.2'}
          d="M179.94 23.5197C186.595 9.63927 200.624 0.809304 216.018 0.812968L651.277 0.916555C666.67 0.920219 680.696 9.75686 687.344 23.6405L862.345 389.086C867.579 400.015 867.576 412.728 862.337 423.655L687.162 789.017C680.507 802.898 666.477 811.728 651.084 811.724L215.825 811.621C200.432 811.617 186.406 802.78 179.758 788.897L4.75677 423.451C-0.477024 412.522 -0.474002 399.809 4.765 388.882L179.94 23.5197Z"
          fill={reversed ? hex : theme.colors.white}
        />
      </svg>
    </StyledHex>
  )
}

const animatedGradient = keyframes`
  0% {
    background-position: 0% 35% 100%;
  }
  50% {
    background-position: 50% 100% 35%;
  }
  100% {
    background-position: 35% 50% 0%;
  }
`

const animatedTransform = keyframes`
  0% {
    transform: scale(1);
    -webkit-transform: scale(1);
  }
  100% {
    transform: scale(2);
    -webkit-transform: scale(1.1);
  }
`

export const StyledContainer = styled.div.withConfig({
  shouldForwardProp: prop => !['useWindow', 'reversed'].includes(prop),
})`
  background: ${props =>
    props.reversed
      ? props.theme.mode === 'light'
        ? theme.colors.white
        : theme.colors.darkModeHighBlack
      : theme.colors.black};
  overflow: hidden;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${props => (props.reversed ? 1 : -1)};

  ${props =>
    props.useWindow &&
    css`
      position: fixed;
    `}
`

export const StyledOverlay = styled.div`
  position: absolute;
  display: block;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  background-image: linear-gradient(to top, ${props => props.primaryColor}, ${props => props.secondaryColor});
  background-size: 120% 120%;
  animation: ${animatedGradient} 3s ease infinite;
  z-index: 0;
`

const hexLength = 80
const hSplit = 1.45
const wSplit = 2

export const StyledHexagons = styled(Hexagons)`
  animation: ${animatedTransform} 20s alternate infinite ease-in;
  height: ${hexLength}%;
  position: absolute;
  width: ${hexLength}%;
  z-index: 1;

[data-theme="dark"] & {
    filter: brightness(0.4);
  }

  ${props =>
    props.x === 1 &&
    css`
      top: -${hexLength / hSplit}%;
      left: -${hexLength / wSplit}%;
    `}
  ${props =>
    props.x === 2 &&
    css`
      bottom: -${hexLength / hSplit}%;
      right: -${hexLength / wSplit}%;
    `}

  ${theme.breakpoints.down('sm')} {
    height: 500px;
    width: 500px;
    ${props =>
      props.x === 1 &&
      css`
        top: -350px;
        left: -180px;
      `}
    ${props =>
      props.x === 2 &&
      css`
        bottom: -350px;
        right: -180px;
      `}
  }
`

export const StyledHex = styled.div`
  position: absolute;
  opacity: ${props => (props.reversed ? '1' : '0.2')};
  width: 100%;
  height: 100%;

  > svg {
    height: 100%;
    width: 100%;
  }

  &:nth-child(2) {
    padding: ${hexLength / 10}%;
  }

  ${theme.breakpoints.down('md')} {
    &:nth-child(2) {
      padding: ${hexLength / 6}%;
    }
  }

  ${theme.breakpoints.down('sm')} {
    &:nth-child(2) {
      padding: 65px;
    }
  }
`
